import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

export const StrategyPanel = ({ companyId }) => {
  const [loading, setLoading] = useState(true);
  const [strategy, setStrategy] = useState(null); // {content, generated_at, updated_at}
  const [draft, setDraft] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let on = true;
    api.get(`/studio/portfolio/${companyId}/strategy`)
      .then(({ data }) => { if (on) { setStrategy(data.strategy); setDraft(data.strategy?.content || ""); } })
      .catch(() => {})
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, [companyId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/studio/portfolio/${companyId}/strategy/generate`);
      setStrategy(data.strategy); setDraft(data.strategy.content); setDirty(false);
      toast.success("Fundraise strategy drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the strategy — try again");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/studio/portfolio/${companyId}/strategy`, { content: draft });
      setStrategy(data.strategy); setDirty(false);
      toast.success("Strategy saved");
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  if (loading) return <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading strategy…</div>;

  return (
    <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4" data-testid={`strategy-panel-${companyId}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
          <Sparkles className="h-3.5 w-3.5" /> Fundraise Strategy {strategy?.updated_at && <span className="normal-case font-medium text-neutral-500">· updated {strategy.updated_at.slice(0, 10)}</span>}
        </span>
        <div className="flex gap-2">
          <button onClick={generate} disabled={generating} data-testid={`strategy-generate-${companyId}`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 border border-amber-500/40 px-3 py-1.5 text-[11px] font-bold text-amber-300 hover:bg-amber-500/25 disabled:opacity-60">
            {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {strategy ? "Regenerate with AI" : "Draft with AI"}
          </button>
          {strategy && (
            <button onClick={save} disabled={saving || !dirty} data-testid={`strategy-save-${companyId}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-[11px] font-bold text-neutral-950 hover:bg-emerald-400 disabled:opacity-40">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save edits
            </button>
          )}
        </div>
      </div>
      {generating && <p className="mt-3 text-sm text-neutral-400">Drafting a mid & long-term plan from the company's CCR analysis — takes ~20 seconds…</p>}
      {!strategy && !generating && (
        <p className="mt-3 text-sm text-neutral-500">No strategy yet. Draft an AI plan — target round, 0–3 / 3–6 / 6–18 month phases and milestone timeline — then edit it to fit your engagement.</p>
      )}
      {strategy && (
        <textarea value={draft} onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
                  data-testid={`strategy-editor-${companyId}`} rows={14} spellCheck={false}
                  className="mt-3 w-full resize-y rounded-lg border border-neutral-800 bg-neutral-950/70 p-3 font-mono text-[12.5px] leading-relaxed text-neutral-300 focus:border-amber-500/50 focus:outline-none" />
      )}
    </div>
  );
};
